/**
 * Push notifications — native-only, web-safe.
 *
 * On the Capacitor Android build (`__NATIVE__`) these drive FCM registration and
 * sync the device token to the API. On web everything no-ops so the app keeps
 * working and the consent UI can show a "mobile only" note.
 */
import { apiClient } from './api-client';

const DEVICE_ID_KEY = 'chortle-device-id';

/** A stable per-install id so a user's multiple devices each get their own token row. */
function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function pushSupported(): boolean {
  return __NATIVE__;
}

/** The device's IANA timezone — briefing schedules fire in this zone. */
export function deviceTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

let listenersReady = false;

/** Register the FCM listeners once. The `registration` event posts the (possibly refreshed) token. */
async function ensureListeners(): Promise<void> {
  if (!__NATIVE__ || listenersReady) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  await PushNotifications.addListener('registration', (token) => {
    void apiClient
      .post('/me/devices', { deviceId: getDeviceId(), pushToken: token.value, platform: 'android' })
      .catch(() => { /* best effort — will retry on next launch */ });
  });
  await PushNotifications.addListener('registrationError', () => {
    /* google-services.json missing or FCM unavailable — silently ignore */
  });

  listenersReady = true;
}

/**
 * On app launch: if the user already consented and the OS permission is still
 * granted, re-register to refresh the token (FCM tokens rotate) and re-sync the
 * briefing timezone if the device has moved zones since the server last saw it.
 */
export async function syncPushOnLaunch(consented: boolean, serverTimezone?: string): Promise<void> {
  if (!__NATIVE__ || !consented) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return;
    await ensureListeners();
    await PushNotifications.register();

    const tz = deviceTimezone();
    if (tz && tz !== serverTimezone) {
      await apiClient
        .patch('/me/notification-preferences', { pushEnabled: true, timezone: tz })
        .catch(() => { /* best effort — will retry on next launch */ });
    }
  } catch {
    /* ignore */
  }
}

/** Turn notifications on: request permission, register, persist consent. Returns true if enabled. */
export async function enablePush(): Promise<boolean> {
  if (!__NATIVE__) return false;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return false;

  await ensureListeners();
  try {
    await PushNotifications.register(); // → 'registration' listener posts the token
  } catch (err) {
    // e.g. Firebase not configured on the device build. Don't claim consent.
    console.error('Push registration failed:', err);
    return false;
  }
  await apiClient.patch('/me/notification-preferences', { pushEnabled: true, timezone: deviceTimezone() });
  return true;
}

/** Turn notifications off: clear consent and remove this device. */
export async function disablePush(): Promise<void> {
  await apiClient.patch('/me/notification-preferences', { pushEnabled: false }).catch(() => {});
  await apiClient.delete(`/me/devices/${getDeviceId()}`).catch(() => {});
  if (__NATIVE__) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await PushNotifications.removeAllListeners();
      listenersReady = false;
    } catch { /* ignore */ }
  }
}
